import { from, Observable } from 'rxjs';
import { debounce, noop } from 'lodash';
import { componentTreeSelectionChanged } from '@ardoq/component-selection';
import { colors } from '@ardoq/design-tokens';
import { enterPressed, moveHighlightRequested } from '@ardoq/dropdown-menu';
import { dispatchAction, reducer, streamReducer } from '@ardoq/rxbeach';
import { Locale } from '@ardoq/locale';
import { SelectComponentItem } from './components/SelectComponentItem';
import { SelectWorkspaceItem } from './components/SelectWorkspaceItem';
import { getOnMouseOverHandler } from '../componentSelector/getOnMouseOverHandler';
import { getPublishScrollHeight } from './routines/getPublishScrollHeight';
import { enhancedScopeDataStreamReducer } from './reducers/selectComponentStreamReducer';
import { getScrollHighlightedIntoViewRoutine } from './routines/getScrollHighlightedIntoViewRoutine';
import { getSelectComponentSelectionChangedRoutine } from './routines/getSelectComponentSelectionChangedRoutine';
import { actionNamespaceSelectComponentHierarchy } from '@ardoq/global-consts';
import { ComponentHierarchyState } from '../componentHierarchyTypes';
import { NavigatorConfiguration } from '../types';
import { navigatorLayoutOperations } from '../reducers/navigatorLayoutOperations';
import { navigatorSelectionOperations } from '../reducers/navigatorSelectionOperations';
import { navigatorOperations } from '../reducers/navigatorOperations';
import { newItemHovered } from '../actions';
import { navigatorTreeOperations } from '../reducers/navigatorTreeOperations';
import { getQuerySelectComponent$ } from './streams/querySelectComponent$';
import { enhancedScopeDataToHierarchyStreamNoDnD } from './enhancedScopeDataToHierarchyStreamNoDnD';
import { getSelectComponentHierarchySelection$ } from '../streams/selectComponentHierarchySelection$';
import { getHandleEnterPressedMultiselectRoutine } from './routines/getHandleEnterPressedMultiselectRoutine';
import {
  containerHeightChanged,
  initialComponentsSelectedInTree,
} from '../actions/navigatorActions';
import { getHandleSelectedComponentsOnOpeningDropdownRoutine } from './routines/getHandleSelectedComponentsOnOpeningDropdownRoutine';
import { getScopeDataStreamWithWorkspaceOptions } from '../streams/getScopeDataStreamWithWorkspaceOptions';

const querySelectComponent$ = getQuerySelectComponent$(
  actionNamespaceSelectComponentHierarchy
);

const {
  enhancedScopeData$,
  loadingScopeDataNotification$,
  loadScopeDataError$,
  workspaceOptions$,
} = getScopeDataStreamWithWorkspaceOptions(
  querySelectComponent$,
  actionNamespaceSelectComponentHierarchy
);

const componentSelection$ = getSelectComponentHierarchySelection$(
  actionNamespaceSelectComponentHierarchy
);

export const selectComponentsInDropdownWorkspaceOptions$ = workspaceOptions$;

const dispatchContainerHeightChanged = () =>
  dispatchAction(
    containerHeightChanged(),
    actionNamespaceSelectComponentHierarchy
  );

const debouncedHeightChanged = debounce(dispatchContainerHeightChanged, 16);

export const getSelectComponentHierarchyConfiguration = (
  locale: Locale
): NavigatorConfiguration => {
  const selectComponentHierarchy$: Observable<ComponentHierarchyState> =
    enhancedScopeDataToHierarchyStreamNoDnD({
      enhancedScopeData$,
      locale,
      isStrictSelection: true,
    });

  return {
    isMultiselectable: true,
    eventHandlers: {
      // The component is 'preselected' by hovering it so we already know which
      // component to select on enter press.
      onComponentClick: componentId =>
        dispatchAction(
          enterPressed({ nodeId: componentId, withMetaKey: false }),
          actionNamespaceSelectComponentHierarchy
        ),
      onWorkspaceClick: noop,
      onScenarioClick: noop,
      onWorkspaceCloseClick: noop,
      onScenarioCloseClick: noop,
      onComponentContextMenu: null,
      onWorkspaceContextmenu: null,
      onScenarioContextmenu: noop,
      onContextMenuClicked: noop,
      onMouseOver: getOnMouseOverHandler(),
    },
    additionalRoutines: [
      getPublishScrollHeight,
      getScrollHighlightedIntoViewRoutine,
      getHandleEnterPressedMultiselectRoutine,
      getSelectComponentSelectionChangedRoutine,
      getHandleSelectedComponentsOnOpeningDropdownRoutine,
    ],
    externalReducers: [
      streamReducer(selectComponentHierarchy$, enhancedScopeDataStreamReducer),
      streamReducer(
        from([{ isViewpointMode: true }]),
        navigatorLayoutOperations.setIsViewpointMode
      ),

      streamReducer(
        componentSelection$,
        navigatorSelectionOperations.updateSelection
      ),
      streamReducer(
        loadingScopeDataNotification$,
        navigatorOperations.setDataLoadingMessage
      ),
      streamReducer(
        loadScopeDataError$,
        navigatorOperations.setDataErrorMessage
      ),
      reducer(newItemHovered, navigatorTreeOperations.newItemHovered),
      reducer(moveHighlightRequested, navigatorTreeOperations.moveHighlight),
      reducer(
        initialComponentsSelectedInTree,
        navigatorLayoutOperations.initialComponentsSelectedInTree
      ),
    ],
    componentSelection$,
    rangeSelectionMode: 'extendSelection',
    actionNamespace: actionNamespaceSelectComponentHierarchy,
    showWorkspaceCloseButton: true,
    showStartCreateReferenceIcon: false,
    backgroundColor: colors.white950,
    components: {
      ComponentItem: SelectComponentItem,
      WorkspaceItem: SelectWorkspaceItem,
      ScenarioItem: () => null,
    },
    onViewDestroyed: () => {
      dispatchAction(
        componentTreeSelectionChanged({ treeSelection: [] }),
        actionNamespaceSelectComponentHierarchy
      );
    },
    isControlledContainerHeight: false,
    useClassNameForRootComponents: true,
    onRootResize: debouncedHeightChanged,
  };
};
